// Story 1.27 — first-ever admin SPA route guard.
//
// Wraps /admin/* routes. Reads the session via useAccountMe(); while
// the query is pending, renders a spinner placeholder; on error or
// is_admin === false, redirects to / with a toast.
//
// 2FA rework (2026-08-17, migration 0072): admin access additionally
// requires PROVEN TOTP. Two soft-gate states before children render:
//   * admin without enrollment → card pointing at the settings 2FA
//     section (/account/preferences). Login itself no longer blocks.
//   * admin enrolled, but THIS session never proved a code (e.g. a
//     session from another browser predating enrollment) → inline
//     step-up prompt that POSTs /auth/2fa-verify.
//
// Defense in depth: this guard is a UX-only gate. The backend's
// AdminUser extractor (server/src/middleware/admin_extractor.rs) is
// the real authorization boundary — every /admin/* API endpoint
// independently enforces is_admin + enrolled + session-proven TOTP.

import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import toast from "react-hot-toast";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Input, Label } from "@greenhold/components";

import { useAccountMe } from "../features/admin/useAccountMe";
import {
  useTwoFactorVerify,
  type TwoFactorError,
} from "../features/two-factor/useTwoFactor";

type Props = {
  children: ReactNode;
};

function GateCard({ children }: { children: ReactNode }) {
  return (
    <main className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-6 text-brand-paper-100">
      <div className="rounded-lg border border-brand-gold-500/40 bg-card/40 p-6 space-y-4">
        {children}
      </div>
    </main>
  );
}

function StepUpPrompt() {
  const queryClient = useQueryClient();
  const verify = useTwoFactorVerify();
  const [code, setCode] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await verify.mutateAsync({ code: code.trim() });
      await queryClient.invalidateQueries({ queryKey: ["account-me"] });
      // session_totp_verified flips to true on refetch; children render.
    } catch (err) {
      const e = err as TwoFactorError;
      toast.error(
        e.code === "TWO_FACTOR_INVALID"
          ? "That code didn't match. Try again."
          : e.message,
      );
      setCode("");
    }
  };

  return (
    <GateCard>
      <h1 className="font-heading text-2xl m-0 text-brand-gold-300">
        Verify to enter the admin area
      </h1>
      <p className="font-body text-sm opacity-80 m-0 leading-relaxed">
        This session hasn't confirmed your second factor yet. Enter a code
        from your authenticator app (or a backup code).
      </p>
      <form onSubmit={handleSubmit} className="flex items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="admin-step-up-code">Code</Label>
          <Input
            id="admin-step-up-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoFocus
            disabled={verify.isPending}
            className="w-36"
          />
        </div>
        <Button
          type="submit"
          variant="primary"
          disabled={verify.isPending || code.trim().length === 0}
          aria-busy={verify.isPending}
          data-testid="admin-step-up-submit"
        >
          {verify.isPending ? "Verifying…" : "Verify"}
        </Button>
      </form>
    </GateCard>
  );
}

export function AdminGuard({ children }: Props) {
  const navigate = useNavigate();
  const query = useAccountMe();

  useEffect(() => {
    if (query.isError) {
      toast.error("Admin access required");
      navigate("/", { replace: true });
    } else if (query.data && !query.data.data.is_admin) {
      toast.error("Admin access required");
      navigate("/", { replace: true });
    }
  }, [query.isError, query.data, navigate]);

  if (query.isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-forest-900 text-brand-paper-100">
        <p className="opacity-70 text-sm font-mono">Loading admin session…</p>
      </div>
    );
  }

  if (query.isError) {
    return <Navigate to="/" replace />;
  }

  const me = query.data?.data;
  if (!me?.is_admin) {
    return <Navigate to="/" replace />;
  }

  // `?? false` — an old cached payload without the 2FA fields must gate,
  // not grant; the refetch after invalidation carries them.
  const enrolled = me.totp_enrolled ?? false;
  const sessionVerified = me.session_totp_verified ?? false;

  if (!enrolled) {
    return (
      <GateCard>
        <h1 className="font-heading text-2xl m-0 text-brand-gold-300">
          Two-factor authentication required
        </h1>
        <p
          className="font-body text-sm opacity-80 m-0 leading-relaxed"
          data-testid="admin-2fa-enrollment-required"
        >
          The admin area stays locked until your account has two-factor
          authentication set up.
        </p>
        <p className="font-body text-sm m-0">
          <Link
            to="/account/preferences"
            className="underline hover:text-brand-gold-300"
          >
            Set it up in Preferences → Two-factor authentication
          </Link>
        </p>
      </GateCard>
    );
  }

  if (!sessionVerified) {
    return <StepUpPrompt />;
  }

  return <>{children}</>;
}
